<template>
  <main class="md:pt-10">
    <UiFixedHeader>
      <template #default="{ show }">
        <UiTitle v-if="show" severity="h5" class="line-clamp-1">
          {{ venue?.title }}
        </UiTitle>
      </template>
    </UiFixedHeader>
    <UiContainer class="mb-4 flex items-center gap-2 pt-4 md:mb-7 lg:gap-3">
      <VenueBaseInfo :value="venue" :loading="!initialized" class="relative" />
    </UiContainer>
    <VenueRepertoire
      :id="GlobalIds['venueRepertoire']"
      class="md:!mb-0"
      :value="venue"
      :loading="!initialized"
      :loading-repertoire="!repertoireLoaded"
      :active-date="activeDate"
      :all-repertoire-loaded="allRepertoireLoaded"
      :repertoire-page-loaded="repertoireNextPageLoaded"
      :repertoire-load-error="repertoireLoadError"
      :repertoire-load-page-error="repertoireLoadPageError"
      @show-timetable="openTimetable"
      @update:active-date="changeActiveDate"
      @load-more="loadRepertoireNextPage(pageFilters)"
      @reload-date="getRepertoire(pageFilters)"
    />
    <UiHorizontalRule />
    <VenueAddressInfo :value="venue" :loading="!initialized" class="pt-2 md:py-0" />
    <UiDialog
      :model-value="showEventTimetable"
      :position="isLgAndUp ? 'middle' : 'bottom'"
      :fit="isLgAndUp"
      :swipe-close="isLgAndDown"
      @update:model-value="closeTimetable"
    >
      <template #default="{ numberHeight }">
        <div
          :style="{ height: `${numberHeight - 120}px` }"
          class="relative size-full rounded-t-3xl bg-primary lg:w-screen lg:max-w-[744px] lg:rounded-3xl"
        >
          <button
            type="button"
            class="!absolute right-6 top-2 z-20 flex size-10 items-center justify-center !p-0 lg:-right-9 lg:-top-3 lg:size-9 lg:min-w-9"
            @click="closeTimetable"
          >
            <UiIcon name="x-mark" class="size-4.5 md:size-6" />
          </button>
          <EventTimetable
            :value="eventForTimetable"
            :loading="!eventInitialized"
            :active-date="timetableEventDate"
            show-title-on-venue
            class="scrollbar-hidden h-full overflow-y-scroll !rounded-t-3xl"
          />
        </div>
      </template>
    </UiDialog>
  </main>
</template>

<script setup lang="ts">
import { isSameDay } from 'date-fns'
import cloneDeep from 'lodash/cloneDeep.js'
import { storeToRefs } from 'pinia'
import type { Undefinable } from 'ts-helpers'
import { computed, defineAsyncComponent, onBeforeMount, ref, shallowRef, watch } from 'vue'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { VenueAddressInfo, VenueBaseInfo, VenueRepertoire } from '@/3_widgets/Venue'
import { useReloadOnConfigurationUpdate } from '@/5_entities/Configuration'
import { type Event, useEvent } from '@/5_entities/Event'
import { useVenue } from '@/5_entities/Venue'
import { usePageFilters, useQueryParamsOpenState } from '@/6_shared/composables'
import { GlobalIds } from '@/6_shared/config'
import { useBreakpoint } from '@/6_shared/lib'
import {
  UiContainer,
  UiDialog,
  UiFixedHeader,
  UiHorizontalRule,
  UiIcon,
  UiTitle
} from '@/6_shared/ui'

const EventTimetable = defineAsyncComponent(
  async () => (await import('@/3_widgets/Event')).EventTimetable
)

const route = useRoute()
const { getVenue, getRepertoire, loadRepertoireNextPage } = useVenue()
const {
  venue,
  initialized,
  allRepertoireLoaded,
  repertoireLoaded,
  repertoireNextPageLoaded,
  repertoireLoadError,
  repertoireLoadPageError
} = storeToRefs(useVenue())
const {
  lg: [isLgAndUp, isLgAndDown]
} = useBreakpoint()
const initPage = (id: number) => {
  getVenue(Number(id))
  openTimeTableFromQuery()
}

useReloadOnConfigurationUpdate(() => initPage(Number(route.params.id as string)))
onBeforeMount(() => {
  initPage(Number(route.params.id as string))
})
onBeforeRouteUpdate(({ params: { id: toId } }, { params: { id: fromId } }) => {
  if (fromId !== toId) {
    initPage(Number(toId as string))
  }
})

const showEventTimetable = ref(false)
const { getEvent } = useEvent()
const { event, eventInitialized } = storeToRefs(useEvent())
const timetableEventDate = ref<Undefinable<string>>(undefined)
const eventForTimetable = computed(() => {
  if (!event.value || !venue.value) return

  const newEvent = cloneDeep(event.value)
  newEvent.seances = newEvent.seances.filter((item) => item?.venue?.id === venue.value?.id)

  return newEvent
})
const { setParamInRouteQuery: changeEventTimetableQuery } = useQueryParamsOpenState(
  'timetable',
  () => {
    // внутри открываются другие модалки.
    // так что нужно проверять, что закрылись именно они, а не текущая
    if (showEventTimetable.value && !route.query?.timetable) {
      showEventTimetable.value = false
    }

    if (!showEventTimetable.value && route.query?.timetable) openTimeTableFromQuery()
  }
)
const openTimetable = (eventId: Event['id'], date: string) => {
  timetableEventDate.value = date
  getEvent(eventId)
  showEventTimetable.value = true

  changeEventTimetableQuery(`${eventId}:${timetableEventDate.value}`)
}
const openTimeTableFromQuery = () => {
  if (route.query.timetable) {
    const [eventId, activeDate] = (route.query.timetable as string).split(':')
    openTimetable(Number(eventId), activeDate)
  }
}
const closeTimetable = () => {
  showEventTimetable.value = false
  changeEventTimetableQuery(undefined)
}

// active date
const { pageFilters, updateDateFilters } = usePageFilters(10)
const activeDate = shallowRef<Date | Date[] | undefined>(undefined)
watch(
  () => venue.value,
  (val) => val?.availableDates?.length && changeActiveDate(val.availableDates[0])
)

const changeActiveDate = (date: Date | Date[]) => {
  if (
    activeDate.value &&
    !Array.isArray(date) &&
    !Array.isArray(activeDate.value) &&
    isSameDay(date, activeDate.value)
  )
    return
  activeDate.value = date
  updateDateFilters(date)
  getRepertoire(pageFilters.value)
}
</script>
